const NOTIFICATION_TEMPLATE_CODE_LIMIT = 120;

/**
 * Materialize the stable identity used by system templates bound to managed
 * events. Keep this in contracts so database seeds and web selection cannot
 * drift. The canonical event name is the only input to the code token.
 */
export function canonicalNotificationEventToken(eventName) {
    const value = String(eventName || '').trim();
    if (!value) throw new TypeError('Managed notification event name is required');
    return value
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .replace(/[^A-Za-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .toUpperCase();
}

export function managedEventTemplateCode(eventName) {
    const token = canonicalNotificationEventToken(eventName);
    const prefix = 'POLICY_EVENT_';
    const code = `${prefix}${token}`;
    return code.length <= NOTIFICATION_TEMPLATE_CODE_LIMIT
        ? code
        : code.slice(0, NOTIFICATION_TEMPLATE_CODE_LIMIT);
}

/** Fail closed when truncation makes two managed events share an identity. */
export function assertUniqueManagedEventTemplateCodes(definitionsOrNames = []) {
    const entries = definitionsOrNames.map((entry) => {
        const eventName = typeof entry === 'string' ? entry : entry?.name;
        return [eventName, managedEventTemplateCode(eventName)];
    });
    const byCode = new Map();
    for (const [eventName, code] of entries) {
        const previous = byCode.get(code);
        if (previous && previous !== eventName) {
            throw new Error(`Managed notification template code collision: ${previous} and ${eventName} -> ${code}`);
        }
        byCode.set(code, eventName);
    }
    return Object.freeze(Object.fromEntries(entries));
}

export function materializeManagedEventTemplateIdentity(definition) {
    const eventName = typeof definition === 'string' ? definition : definition?.name;
    const label = typeof definition === 'string' ? null : definition?.label;
    const name = label || { tr: eventName, en: eventName };
    return {
        code: managedEventTemplateCode(eventName),
        name: {
            tr: String(name.tr || name.en || eventName).slice(0, NOTIFICATION_TEMPLATE_CODE_LIMIT),
            en: String(name.en || name.tr || eventName).slice(0, NOTIFICATION_TEMPLATE_CODE_LIMIT),
        },
        binding: { kind: 'event', eventName: String(eventName) },
    };
}

export const APPLICATION_NOTIFICATION_TEMPLATE_CODES = Object.freeze({
    USER_WELCOME: 'USER_WELCOME',
    USER_LOGIN: 'USER_LOGIN',
    USER_PASSWORD_CHANGED: 'USER_PASSWORD_CHANGED',
    USER_PASSWORD_RESET: 'USER_PASSWORD_RESET',
    DETAILED_REPORT_READY: 'DETAILED_REPORT_READY',
});

/** @deprecated Prefer APPLICATION_NOTIFICATION_TEMPLATE_CODES; kept for seed/mail backfill. */
export const LEGACY_NOTIFICATION_TEMPLATE_CODE_ALIASES = Object.freeze({
    WELCOME: APPLICATION_NOTIFICATION_TEMPLATE_CODES.USER_WELCOME,
    PASSWORD_RESET: APPLICATION_NOTIFICATION_TEMPLATE_CODES.USER_PASSWORD_RESET,
});

function variable(def) {
    return Object.freeze({
        key: def.key,
        type: def.type || 'string',
        label: Object.freeze(def.label),
        description: Object.freeze(def.description),
        example: def.example ?? '',
        required: def.required !== false,
    });
}

export const APPLICATION_NOTIFICATION_TEMPLATE_CATALOG = Object.freeze([
    Object.freeze({
        code: APPLICATION_NOTIFICATION_TEMPLATE_CODES.USER_WELCOME,
        category: 'identity',
        channels: Object.freeze(['email']),
        variables: Object.freeze([
            variable({
                key: 'userName',
                label: { tr: 'Kullanıcı adı', en: 'Username' },
                description: {
                    tr: 'Hesabın giriş adı veya e-posta adresi.',
                    en: 'Account login name or email address.',
                },
                example: 'ada.yilmaz',
            }),
            variable({
                key: 'firstName',
                label: { tr: 'Ad', en: 'First name' },
                description: {
                    tr: 'Kullanıcının görünen adı.',
                    en: 'User given / first name.',
                },
                example: 'Ada',
                required: false,
            }),
        ]),
    }),
    Object.freeze({
        code: APPLICATION_NOTIFICATION_TEMPLATE_CODES.USER_LOGIN,
        category: 'identity',
        channels: Object.freeze(['in-app']),
        variables: Object.freeze([
            variable({
                key: 'email',
                label: { tr: 'E-posta', en: 'Email' },
                description: {
                    tr: 'Giriş yapan hesabın e-posta adresi.',
                    en: 'Email of the account that signed in.',
                },
                example: 'ada@example.com',
            }),
            variable({
                key: 'loginAt',
                type: 'date',
                label: { tr: 'Giriş zamanı', en: 'Login time' },
                description: {
                    tr: 'ISO-8601 giriş zaman damgası.',
                    en: 'ISO-8601 login timestamp.',
                },
                example: '2026-08-01T12:00:00.000Z',
            }),
            variable({
                key: 'ipAddress',
                label: { tr: 'IP adresi', en: 'IP address' },
                description: {
                    tr: 'İstemci IP adresi (varsa).',
                    en: 'Client IP address when available.',
                },
                example: '203.0.113.10',
                required: false,
            }),
            variable({
                key: 'authMethod',
                label: { tr: 'Kimlik yöntemi', en: 'Auth method' },
                description: {
                    tr: 'password, sso, mfa gibi kimlik doğrulama yöntemi.',
                    en: 'Authentication method such as password, sso, or mfa.',
                },
                example: 'password',
                required: false,
            }),
        ]),
    }),
    Object.freeze({
        code: APPLICATION_NOTIFICATION_TEMPLATE_CODES.USER_PASSWORD_CHANGED,
        category: 'identity',
        channels: Object.freeze(['email']),
        variables: Object.freeze([
            variable({
                key: 'source',
                label: { tr: 'Kaynak', en: 'Source' },
                description: {
                    tr: 'Değişikliğin kaynağı (self, admin, reset).',
                    en: 'Where the change came from (self, admin, reset).',
                },
                example: 'self',
            }),
            variable({
                key: 'changedAt',
                type: 'date',
                label: { tr: 'Değişim zamanı', en: 'Changed at' },
                description: {
                    tr: 'ISO-8601 parola değişim zamanı.',
                    en: 'ISO-8601 password change timestamp.',
                },
                example: '2026-08-01T12:05:00.000Z',
            }),
        ]),
    }),
    Object.freeze({
        code: APPLICATION_NOTIFICATION_TEMPLATE_CODES.USER_PASSWORD_RESET,
        category: 'identity',
        channels: Object.freeze(['email']),
        variables: Object.freeze([
            variable({
                key: 'resetToken',
                label: { tr: 'Sıfırlama jetonu', en: 'Reset token' },
                description: {
                    tr: 'Tek kullanımlık sıfırlama jetonu (e-posta bağlantısında kullanılır).',
                    en: 'One-time reset token used in the email link.',
                },
                example: 'sample-reset-token',
            }),
            variable({
                key: 'expiresAt',
                type: 'date',
                label: { tr: 'Son geçerlilik', en: 'Expires at' },
                description: {
                    tr: 'Jetonun geçerlilik bitiş zamanı.',
                    en: 'When the reset token expires.',
                },
                example: '2026-08-01T13:00:00.000Z',
            }),
        ]),
    }),
    Object.freeze({
        code: APPLICATION_NOTIFICATION_TEMPLATE_CODES.DETAILED_REPORT_READY,
        category: 'reporting',
        channels: Object.freeze(['in-app', 'email', 'sms', 'push']),
        variables: Object.freeze([
            variable({
                key: 'reportName',
                label: { tr: 'Rapor adı', en: 'Report name' },
                description: {
                    tr: 'Hazırlanan kontrollü raporun adı.',
                    en: 'Name of the controlled report that is ready.',
                },
                example: 'Risk özeti',
            }),
            variable({
                key: 'reportId',
                label: { tr: 'Rapor kimliği', en: 'Report ID' },
                description: {
                    tr: 'Rapor kaydının kimliği.',
                    en: 'Identifier of the report record.',
                },
                example: '507f1f77bcf86cd799439011',
            }),
            variable({
                key: 'downloadUrl',
                label: { tr: 'İndirme bağlantısı', en: 'Download URL' },
                description: {
                    tr: 'Kimlik doğrulamalı indirme URL’si. HTML gövdede href için kaçışa dikkat edin.',
                    en: 'Authenticated download URL. Escape carefully if used inside HTML href.',
                },
                example: 'https://app.example.com/reports/download/sample',
            }),
            variable({
                key: 'expiresAt',
                type: 'date',
                label: { tr: 'Bağlantı bitişi', en: 'Link expires at' },
                description: {
                    tr: 'İndirme bağlantısının son geçerlilik zamanı.',
                    en: 'When the download link expires.',
                },
                example: '2026-08-02T12:00:00.000Z',
            }),
        ]),
    }),
]);

const applicationTemplateByCode = new Map(
    APPLICATION_NOTIFICATION_TEMPLATE_CATALOG.map((template) => [template.code, template]),
);

export function normalizeApplicationNotificationTemplateCode(code) {
    const normalized = String(code || '').trim().toUpperCase();
    return LEGACY_NOTIFICATION_TEMPLATE_CODE_ALIASES[normalized] || normalized;
}

export function getApplicationNotificationTemplate(code) {
    return applicationTemplateByCode.get(normalizeApplicationNotificationTemplateCode(code)) || null;
}

export function isApplicationNotificationTemplateCode(code) {
    return applicationTemplateByCode.has(normalizeApplicationNotificationTemplateCode(code));
}

export function getApplicationNotificationVariableKeys(codeOrTemplate) {
    const template = typeof codeOrTemplate === 'string'
        ? getApplicationNotificationTemplate(codeOrTemplate)
        : codeOrTemplate;
    return (template?.variables || []).map((entry) => (
        typeof entry === 'string' ? entry : entry.key
    ));
}

export function buildApplicationNotificationSampleVariables(code) {
    const template = getApplicationNotificationTemplate(code);
    if (!template) throw new Error(`Unknown application notification template code: ${code}`);
    return Object.fromEntries(
        template.variables.map((entry) => [entry.key, entry.example ?? '']),
    );
}

export function buildApplicationNotificationVariables(code, values = {}) {
    const template = getApplicationNotificationTemplate(code);
    if (!template) throw new Error(`Unknown application notification template code: ${code}`);
    return Object.fromEntries(
        template.variables.map((entry) => [entry.key, values?.[entry.key]]),
    );
}

/** Extract Handlebars-style `{{token}}` names (simple paths, no helpers). */
export function extractHandlebarsVariableTokens(source) {
    const text = String(source || '');
    const tokens = new Set();
    const pattern = /\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/g;
    let match = pattern.exec(text);
    while (match) {
        tokens.add(match[1]);
        match = pattern.exec(text);
    }
    return [...tokens];
}

export function lintNotificationTemplateVariables({ declared = [], variants = {} } = {}) {
    const declaredSet = new Set((declared || []).map(String));
    const used = new Set();
    const walk = (value) => {
        if (typeof value === 'string') {
            for (const token of extractHandlebarsVariableTokens(value)) used.add(token);
            return;
        }
        if (!value || typeof value !== 'object') return;
        for (const nested of Object.values(value)) walk(nested);
    };
    walk(variants);
    const undeclared = [...used].filter((token) => !declaredSet.has(token)).sort();
    const unused = [...declaredSet].filter((token) => !used.has(token)).sort();
    return { used: [...used].sort(), undeclared, unused };
}
